import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  User,
  DollarSign,
  Calendar,
  FileText,
  Eye,
  Users,
  TrendingUp,
  AlertCircle,
  Shield,
  Plus,
  Edit
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import { useCompensationValidation } from '@/hooks/payslips';
import { useAuth } from '@/hooks/useAuth';
import { useHasPermission } from '@/hooks/permissions/useHasPermission';
import { ValidationRulesList } from './ValidationRulesList';
import { ApprovalWorkflowCard } from './ApprovalWorkflowCard';
import type { CompensationRule } from '@/services/compensation/types';
import type { PayrollImpactEstimate } from '@/services/payslips/types';

interface PendingChange {
  id: string;
  type: 'create' | 'update' | 'delete';
  rule: CompensationRule;
  originalRule?: CompensationRule;
  submittedBy: {
    id: string;
    name: string;
    role: string;
  };
  submittedAt: string;
  status: 'pending' | 'approved' | 'rejected';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  reason?: string;
  impactEstimate?: PayrollImpactEstimate;
}

interface CompensationChangeApprovalProps {
  pendingChanges: PendingChange[];
  onApprove: (changeId: string, approvalReason?: string) => Promise<void>;
  onReject: (changeId: string, rejectionReason: string) => Promise<void>;
  onBulkAction: (changeIds: string[], action: 'approve' | 'reject', reason?: string) => Promise<void>;
  companyId: string;
  isLoading?: boolean;
}

export function CompensationChangeApproval({
  pendingChanges,
  onApprove,
  onReject,
  onBulkAction,
  companyId,
  isLoading = false
}: CompensationChangeApprovalProps) {
  const { profile } = useAuth();
  const [selectedChanges, setSelectedChanges] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedChange, setSelectedChange] = useState<PendingChange | null>(null);
  const [approvalReason, setApprovalReason] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showReasonRequired, setShowReasonRequired] = useState(false);

  // Get validation for selected changes
  const selectedChangesArray = pendingChanges.filter(change => 
    selectedChanges.has(change.id) && change.status === 'pending'
  );
  
  const {
    data: validationResults,
    isLoading: validationLoading
  } = useCompensationValidation(
    companyId,
    selectedChangesArray.map(change => change.rule)
  );

  useEffect(() => {
    if (selectedChange && selectedChange.priority === 'urgent') {
      setShowReasonRequired(true);
    }
  }, [selectedChange]);

  const { currencyCode } = useWorkplaceCurrency();
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  const getPriorityColor = (priority: PendingChange['priority']) => {
    switch (priority) {
      case 'urgent':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'high':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'low':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getChangeTypeIcon = (type: PendingChange['type']) => {
    switch (type) {
      case 'create':
        return <Plus className="h-4 w-4 text-green-600" />;
      case 'update':
        return <FileText className="h-4 w-4 text-blue-600" />;
      case 'delete':
        return <XCircle className="h-4 w-4 text-red-600" />;
    }
  };

  const getChangeDescription = (change: PendingChange) => {
    const { type, rule, originalRule } = change;
    const employeeName = 'Employee'; // Employee name resolution handled at assignment level
    
    switch (type) {
      case 'create':
        return `New ${rule.rule_type} rule for ${employeeName}`;
      case 'update':
        return `Update ${rule.rule_type} rule for ${employeeName}`;
      case 'delete':
        return `Delete ${rule.rule_type} rule for ${employeeName}`;
      default:
        return 'Unknown change type';
    }
  };

  const handleSelectChange = (changeId: string, selected: boolean) => {
    const newSelected = new Set(selectedChanges);
    if (selected) {
      newSelected.add(changeId);
    } else {
      newSelected.delete(changeId);
    }
    setSelectedChanges(newSelected);
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      const pendingIds = pendingChanges
        .filter(change => change.status === 'pending')
        .map(change => change.id);
      setSelectedChanges(new Set(pendingIds));
    } else {
      setSelectedChanges(new Set());
    }
  };

  const handleApprove = async (changeId: string) => {
    try {
      await onApprove(changeId, approvalReason || undefined);
      setApprovalReason('');
      setSelectedChange(null);
    } catch (error) {
      console.error('Failed to approve change:', error);
    }
  };

  const handleReject = async (changeId: string) => {
    if (!rejectionReason.trim()) {
      setShowReasonRequired(true);
      return;
    }
    
    try {
      await onReject(changeId, rejectionReason);
      setRejectionReason('');
      setSelectedChange(null);
      setShowReasonRequired(false);
    } catch (error) {
      console.error('Failed to reject change:', error);
    }
  };

  const handleBulkApprove = async () => {
    const changeIds = Array.from(selectedChanges);
    await onBulkAction(changeIds, 'approve', approvalReason || undefined);
    setSelectedChanges(new Set());
    setApprovalReason('');
  };

  const handleBulkReject = async () => {
    if (!rejectionReason.trim()) {
      setShowReasonRequired(true);
      return;
    }
    
    const changeIds = Array.from(selectedChanges);
    await onBulkAction(changeIds, 'reject', rejectionReason);
    setSelectedChanges(new Set());
    setRejectionReason('');
    setShowReasonRequired(false);
  };

  const pendingCount = pendingChanges.filter(c => c.status === 'pending').length;
  const approvedCount = pendingChanges.filter(c => c.status === 'approved').length;
  const rejectedCount = pendingChanges.filter(c => c.status === 'rejected').length;

  // Use permission system for compensation approval access
  const canManageEmployeePay = useHasPermission('manage_employee_pay');

  if (!canManageEmployeePay) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="text-center text-muted-foreground">
            <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium mb-2">Access Denied</h3>
            <p>You need manager or admin permissions to view compensation change approvals.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-orange-600" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">{pendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Approved</p>
                <p className="text-2xl font-bold">{approvedCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <XCircle className="h-8 w-8 text-red-600" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Rejected</p>
                <p className="text-2xl font-bold">{rejectedCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total</p>
                <p className="text-2xl font-bold">{pendingChanges.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="pending">
              Pending ({pendingCount})
            </TabsTrigger>
            <TabsTrigger value="approved">
              Approved ({approvedCount})
            </TabsTrigger>
            <TabsTrigger value="rejected">
              Rejected ({rejectedCount})
            </TabsTrigger>
            <TabsTrigger value="validation">
              Validation
            </TabsTrigger>
          </TabsList>

          {/* Bulk Actions */}
          {selectedChanges.size > 0 && activeTab === 'pending' && (
            <div className="flex items-center gap-2">
              <Badge variant="secondary">
                {selectedChanges.size} selected
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkApprove}
                disabled={isLoading}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve Selected
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkReject}
                disabled={isLoading}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject Selected
              </Button>
            </div>
          )}
        </div>

        {/* Pending Changes */}
        <TabsContent value="pending" className="space-y-4">
          {pendingCount === 0 ? (
            <Card>
              <CardContent className="p-8">
                <div className="text-center text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-medium mb-2">No Pending Changes</h3>
                  <p>All compensation changes have been reviewed.</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Select All */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={selectedChanges.size === pendingCount && pendingCount > 0}
                      onCheckedChange={(checked) => handleSelectAll(!!checked)}
                    />
                    <Label className="font-medium">
                      Select all pending changes ({pendingCount})
                    </Label>
                  </div>
                </CardContent>
              </Card>

              {/* Changes List */}
              <div className="space-y-4">
                {pendingChanges
                  .filter(change => change.status === 'pending')
                  .map((change) => (
                    <ApprovalWorkflowCard
                      key={change.id}
                      change={change}
                      isSelected={selectedChanges.has(change.id)}
                      onSelect={(selected) => handleSelectChange(change.id, selected)}
                      onApprove={() => handleApprove(change.id)}
                      onReject={() => handleReject(change.id)}
                      onView={() => setSelectedChange(change)}
                      isLoading={isLoading}
                    />
                  ))}
              </div>
            </>
          )}
        </TabsContent>

        {/* Approved Changes */}
        <TabsContent value="approved" className="space-y-4">
          {approvedCount === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No approved changes yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {pendingChanges
                .filter(change => change.status === 'approved')
                .map((change) => (
                  <ApprovalWorkflowCard
                    key={change.id}
                    change={change}
                    isSelected={false}
                    onSelect={() => {}}
                    onApprove={() => {}}
                    onReject={() => {}}
                    onView={() => setSelectedChange(change)}
                    isLoading={false}
                    readonly
                  />
                ))}
            </div>
          )}
        </TabsContent>

        {/* Rejected Changes */}
        <TabsContent value="rejected" className="space-y-4">
          {rejectedCount === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <XCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No rejected changes.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {pendingChanges
                .filter(change => change.status === 'rejected')
                .map((change) => (
                  <ApprovalWorkflowCard
                    key={change.id}
                    change={change}
                    isSelected={false}
                    onSelect={() => {}}
                    onApprove={() => {}}
                    onReject={() => {}}
                    onView={() => setSelectedChange(change)}
                    isLoading={false}
                    readonly
                  />
                ))}
            </div>
          )}
        </TabsContent>

        {/* Validation Results */}
        <TabsContent value="validation" className="space-y-4">
          {selectedChanges.size === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <AlertTriangle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">No Changes Selected</h3>
                <p>Select compensation changes to view validation results.</p>
              </CardContent>
            </Card>
          ) : (
            <ValidationRulesList
              validationResults={validationResults}
              isLoading={validationLoading}
              selectedChanges={selectedChangesArray}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Reason Input (shown when needed) */}
      {showReasonRequired && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-800">
              <AlertCircle className="h-5 w-5" />
              Reason Required
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="rejection-reason">
                Please provide a reason for rejection:
              </Label>
              <Textarea
                id="rejection-reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why these changes are being rejected..."
                className="mt-2"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowReasonRequired(false);
                  setRejectionReason('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (selectedChange) {
                    handleReject(selectedChange.id);
                  } else if (selectedChanges.size > 0) {
                    handleBulkReject();
                  }
                }}
                disabled={!rejectionReason.trim()}
              >
                Confirm Rejection
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}